import {useState} from 'react'
import {Card, Button, Spinner} from 'react-bootstrap'
import {useQuery} from '@apollo/client'
import {ASSIGNABLE_NUMBERS} from '../../gql/queries/twilioNumbers'
import {AssignNumberModal} from '../numbers/components/AssignNumberModal'
import {useIntl} from 'react-intl'
import {useTableSort} from '../../helpers/useTableSort'
import {BILLING_MESSAGES as M, EM_DASH} from './billingMessages'

type Num = {
  id: string
  e164: string
  areaCode: string
  routeMode: string
  ownerName: string
  assignedUserName: string
  assignedGroupName: string
}

const formatE164 = (e164: string) => {
  const m = (e164 || '').match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  return m ? `+1 (${m[1]}) ${m[2]}-${m[3]}` : e164
}

// Admin/supervisor: reassign the routing of numbers in scope (owner keeps paying).
export const AssignmentsTab = () => {
  const intl = useIntl()
  const {data, loading, refetch} = useQuery(ASSIGNABLE_NUMBERS, {fetchPolicy: 'cache-and-network'})
  const rows: Num[] = data?.assignableNumbers ?? []
  const {onSort, sortRows, arrow} = useTableSort<any>('', 'desc')
  const [target, setTarget] = useState<Num | null>(null)

  const assignee = (n: Num) =>
    n.assignedGroupName ? `Group: ${n.assignedGroupName}` : n.assignedUserName || '—'

  return (
    <div className='mt-5'>
      <div className='mb-5'>
        <h2 className='mb-1'>{intl.formatMessage(M.assignmentsTitle)}</h2>
        <span className='text-muted'>{intl.formatMessage(M.assignmentsSubtitle)}</span>
      </div>

      <Card>
        <Card.Body className='p-0'>
          {loading && !data ? (
            <div className='text-center py-10'>
              <Spinner animation='border' size='sm' />
            </div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-dashed align-middle gy-2 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted' style={{cursor: 'pointer'}}>
                    <th className='ps-6' onClick={() => onSort('e164')}>{intl.formatMessage(M.number)}{arrow('e164')}</th>
                    <th onClick={() => onSort('ownerName')}>{intl.formatMessage(M.colOwnerPays)}{arrow('ownerName')}</th>
                    <th onClick={() => onSort('assignedUserName')}>{intl.formatMessage(M.colAssignedTo)}{arrow('assignedUserName')}</th>
                    <th onClick={() => onSort('routeMode')}>{intl.formatMessage(M.colMode)}{arrow('routeMode')}</th>
                    <th className='text-end pe-6'>{intl.formatMessage(M.action)}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortRows(rows).map((n: Num) => (
                    <tr key={n.id}>
                      <td className='ps-6 fw-bold'>{formatE164(n.e164)}</td>
                      <td className='text-muted'>{n.ownerName || EM_DASH}</td>
                      <td>
                        {n.assignedGroupName ? (
                          <span className='badge badge-light-info'>{intl.formatMessage(M.groupBadge, {name: n.assignedGroupName})}</span>
                        ) : n.assignedUserName ? (
                          <span className='badge badge-light-primary'>{n.assignedUserName}</span>
                        ) : (
                          <span className='text-muted'>{EM_DASH}</span>
                        )}
                      </td>
                      <td>
                        <span className='badge badge-light'>{intl.formatMessage(n.routeMode === 'GROUP' ? M.group : M.agent)}</span>
                      </td>
                      <td className='text-end pe-6'>
                        <Button variant='light-primary' size='sm' onClick={() => setTarget(n)}>
                          {intl.formatMessage(M.reassign)}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && (
                <p className='text-muted mb-0 p-6'>{intl.formatMessage(M.assignmentsEmpty)}</p>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <AssignNumberModal
        show={!!target}
        onHide={() => setTarget(null)}
        numberId={target?.id ?? null}
        numberLabel={target ? formatE164(target.e164) : ''}
        currentAssignee={target ? assignee(target) : ''}
        onAssigned={() => refetch()}
      />
    </div>
  )
}
